import DebugService from '../debug.service';
import moment from 'moment-timezone';
import { Cesium } from '../../cesium';
import { Moment } from 'moment';
import {
    transformNaiveDateIntoMoment,
    transformNaiveMomentIntoNaiveDate,
} from 'app/shared/utilities/time';
import { TimeZoneManager } from 'app/shared/time/TimeZoneManager';

const log = (...args) => DebugService.log('Time', ...args);

export class Clock {
    _$window;
    _cesiumClock;

    /**
     * A simple abstraction of the Cesium clock.
     * Use with a clockHandler -> new clockHandler(new Clock(new Cesium.Clock()))
     * @param cesiumClock Cesium clock object
     */
    constructor(cesiumClock, $window) {
        this._$window = $window;
        this._cesiumClock = cesiumClock;

        // If you're changing these, be careful.
        this._cesiumClock.clockStep = Cesium.ClockStep.SYSTEM_CLOCK_MULTIPLIER;
        this._cesiumClock.clockRange = Cesium.ClockRange.CLAMPED;

        const getTimestamp =
            this._$window.performance !== undefined && !!this._$window.performance.now
                ? () => this._$window.performance.now()
                : () => Date.now();

        /*
         * This function always acts as an UNBOUNDED ClockRange, even though the ClockRange is actually CLAMPED
         *
         * This is so when the new currentTime is calculated, it isn't limited by the timeline (the timeline will adjust itself). (UNBOUNDED ClockRange)
         * But the Clock is still CLAMPED to timeline for scrubbing.
         *
         * Another solution to this would be to write our own render loop for Cesium. There's an option when creation the Cesium Viewer to not use the default render loop.
         * That render loop could tick the clock based on timeouts rather than the requestAnimationFrame (which is why it stops when the window loses focus)
         */
        this._cesiumClock.tick = () => {
            const currentSystemTime = getTimestamp();
            let currentTime = Cesium.JulianDate.clone(this._cesiumClock._currentTime);

            if (this._cesiumClock.canAnimate && this._cesiumClock._shouldAnimate) {
                const milliseconds = currentSystemTime - this._cesiumClock._lastSystemTime;
                currentTime = Cesium.JulianDate.addSeconds(
                    currentTime,
                    this._cesiumClock._multiplier * (milliseconds / 1000.0),
                    currentTime,
                );
            }

            this._cesiumClock._currentTime = currentTime;
            this._cesiumClock._lastSystemTime = currentSystemTime;
            this._cesiumClock.onTick.raiseEvent(this._cesiumClock);
            return currentTime;
        };
    }

    getServerTimeNow(timeZoneManager: TimeZoneManager): Moment {
        const m = moment.tz(timeZoneManager.getTimeZone().string);
        const clientTime = transformNaiveMomentIntoNaiveDate(m);
        const serverTime = transformNaiveDateIntoMoment(clientTime, timeZoneManager.getTimeZone());
        return serverTime;
    }

    getCursorTimeAsJulian() {
        return this._cesiumClock ? this._cesiumClock.currentTime : null;
    }

    getCursorTimeAsMoment() {
        // TODO Test the speed of creating moment from String and Julian.toString() vs moment from date and Julian.toDate(julian)
        return this._cesiumClock
            ? moment(Cesium.JulianDate.toDate(this._cesiumClock.currentTime))
            : null;
    }

    setCursorTime(value) {
        if (value instanceof Cesium.JulianDate) {
            this._cesiumClock.currentTime = value;
        } else if (value instanceof moment) {
            this._cesiumClock.currentTime = Cesium.JulianDate.fromDate(
                (value as Moment).toDate(),
                new Cesium.JulianDate(),
            );
        } else {
            throw 'Can only set the current time with a moment or a JulianDate';
        }
    }

    // Start Time

    getStartTimeAsDate() {
        return this._cesiumClock ? Cesium.JulianDate.toDate(this._cesiumClock.startTime) : null;
    }

    getStartTimeAsJulian() {
        return this._cesiumClock ? this._cesiumClock.startTime : null;
    }

    getStartTimeAsMoment() {
        return this._cesiumClock
            ? moment(Cesium.JulianDate.toDate(this._cesiumClock.startTime))
            : null;
    }

    setStartTime(value) {
        if (value instanceof Cesium.JulianDate) {
            this._cesiumClock.startTime = value;
        } else if (value instanceof moment) {
            this._cesiumClock.startTime = Cesium.JulianDate.fromDate(
                (value as Moment).toDate(),
                new Cesium.JulianDate(),
            );
        } else {
            throw 'Can only set the start time with a moment or a JulianDate';
        }
    }

    // End Time

    getEndTimeAsDate() {
        return this._cesiumClock ? Cesium.JulianDate.toDate(this._cesiumClock.stopTime) : null;
    }

    getEndTimeAsJulian() {
        return this._cesiumClock ? this._cesiumClock.stopTime : null;
    }

    getEndTimeAsMoment() {
        return this._cesiumClock
            ? moment(Cesium.JulianDate.toDate(this._cesiumClock.stopTime))
            : null;
    }

    setEndTime(value) {
        if (value instanceof Cesium.JulianDate) {
            this._cesiumClock.stopTime = value;
        } else if (value instanceof moment) {
            this._cesiumClock.stopTime = Cesium.JulianDate.fromDate(
                (value as Moment).toDate(),
                new Cesium.JulianDate(),
            );
        } else {
            throw 'Can only set the end time with a moment or a JulianDate';
        }
    }

    get playSpeed() {
        return this._cesiumClock.multiplier;
    }

    set playSpeed(speed) {
        this._cesiumClock.multiplier = speed;
    }

    isPlaying() {
        return !!(this._cesiumClock.shouldAnimate && this._cesiumClock.canAnimate);
    }

    addTickListener(callback) {
        return this._cesiumClock.onTick.addEventListener(callback);
    }

    play() {
        if (!this.isPlaying()) {
            this._cesiumClock.shouldAnimate = true;
        }
    }

    pause() {
        if (this.isPlaying()) {
            this._cesiumClock.shouldAnimate = false;
        }
    }
}
